/**
 * sl-9qm.22: Doc-Reference Property Type Definition
 *
 * Cell value is a docId string. During materialization, the backend
 * resolves it to a ResolvedDocReference object with title + content.
 */
import { propertyType, t } from '@blocksuite/affine/blocks/database';
import zod from 'zod';

export const docReferencePropertyType = propertyType('doc-reference');

/** Property data schema (column-level configuration) */
export const DocReferencePropertyDataSchema = zod.object({
  renderMode: zod.enum(['excerpt', 'full', 'title-only']).default('excerpt'),
  maxExcerptLength: zod.number().int().min(50).max(2000).optional(),
  showLastModified: zod.boolean().optional(),
});

export type DocReferencePropertyData = zod.infer<
  typeof DocReferencePropertyDataSchema
>;

/** Cell value is a doc ID string (or null for empty) */
export const DocReferenceCellRawValueSchema = zod.string().nullable();
export const DocReferenceCellJsonValueSchema = zod.string().nullable();

export type DocReferenceCellRawValue = zod.infer<
  typeof DocReferenceCellRawValueSchema
>;
export type DocReferenceCellJsonValue = zod.infer<
  typeof DocReferenceCellJsonValueSchema
>;

export const docReferencePropertyModelConfig =
  docReferencePropertyType.modelConfig({
    name: 'Doc Reference',
    propertyData: {
      schema: DocReferencePropertyDataSchema,
      default: () => ({
        renderMode: 'excerpt' as const,
      }),
    },
    rawValue: {
      schema: DocReferenceCellRawValueSchema,
      default: () => null as DocReferenceCellRawValue,
      fromString: ({ value }) => ({
        value: value || null,
      }),
      toString: ({ value }) => value ?? '',
      toJson: ({ value }) => value,
      fromJson: ({ value }) => value,
    },
    jsonValue: {
      schema: DocReferenceCellJsonValueSchema,
      type: () => t.string.instance(),
      isEmpty: ({ value }) => !value,
    },
  });
